import { AccountLevelEnum, CarriersEnum, USPSPaymentAccountTypeEnum } from '@app/app.enum';
import {
	CarriersNotRequiringEmail,
	fullNameRegex,
	OnlyNumberAndLetterRegex,
	OnlyNumberRegex,
	PhoneNumberRegex,
} from '@app/constants';
import * as models from '@app/models/index';
import { ICarrierAccountConfiguration, IFedExNativeConfiguration } from '@app/models/index';
import Injector from '@app/services';
import { IntlService } from '@app/services/intl.service';
import { mergeValidationSchemas } from '@app/utils';
import * as Yup from 'yup';

const intlService: IntlService = Injector.get(IntlService);

export const uspsCarrierAccountInfoSchema = (carrierCode: CarriersEnum, accountLevel: AccountLevelEnum) => {
	let schemaValidation = Yup.object().shape<models.IUSPSConfiguration>({
		name: Yup.string()
			.matches(fullNameRegex, intlService.GetFormattedMessage('app.profile.company-form.fullName-validation'))
			.required(intlService.GetFormattedMessage('app.profile.company-form.name-required-validation')),
		company: Yup.string(),
		city: Yup.string().required(
			intlService.GetFormattedMessage('app.profile.company-form.city-name-required-validation')
		),
		country: Yup.string().required(
			intlService.GetFormattedMessage('app.profile.company-form.country-name-required-validation')
		),
		state: Yup.string().required(
			intlService.GetFormattedMessage('app.profile.company-form.state-name-required-validation')
		),
		street1: Yup.string().required(
			intlService.GetFormattedMessage('app.profile.company-form.street1-required-validation')
		),
		street2: Yup.string().notRequired(),
		street3: Yup.string().notRequired(),
		zip: Yup.string().required(intlService.GetFormattedMessage('app.profile.company-form.zip-required-validation')),
		phone: Yup.string().matches(
			PhoneNumberRegex,
			intlService.GetFormattedMessage('app.profile.company-form.phone-validation')
		),
		email: Yup.string()
			.email(intlService.GetFormattedMessage('app.login.forgot.password.invalid-email-validation'))
			.when([], {
				is: () => CarriersNotRequiringEmail.includes(carrierCode),
				then: Yup.string().notRequired(),
				otherwise: Yup.string().required(
					intlService.GetFormattedMessage('app.login.forgot.password.email-required-validation')
				),
			}),
		logo: Yup.string(),
		mraReturnsMid: Yup.string()
			.notRequired()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation'),
				(val) => !val || (val && (val.toString().length === 6 || val.toString().length === 9))
			)
			.matches(
				OnlyNumberRegex,
				intlService.GetFormattedMessage('app.carrier-account.config.returns-mid-label-type-validation')
			)
			.test(
				'is-empty-or-all-populated',
				intlService.GetFormattedMessage('app.carrier-account.config.mid-account-code-both-required'),
				function (value) {
					const { mraAccountCode } = this.parent;
					const ismraAccountCodeEmpty = !mraAccountCode;
					const isCurrentFieldEmpty = !value;

					if (isCurrentFieldEmpty) {
						return ismraAccountCodeEmpty;
					}

					return true;
				}
			),
		mraAccountCode: Yup.string()
			.notRequired()
			.test(
				'is-empty-or-all-populated',
				intlService.GetFormattedMessage('app.carrier-account.config.mid-account-code-both-required'),
				function (value) {
					const { mraReturnsMid } = this.parent;
					const ismraReturnsMidEmpty = !mraReturnsMid;
					const isCurrentFieldEmpty = !value;

					if (isCurrentFieldEmpty) {
						return ismraReturnsMidEmpty;
					}

					return true;
				}
			)
			.length(32, intlService.GetFormattedMessage('app.carrier-account.config.mra-account-code-length-validation')),
		printAltReturnAddressOnUSPSLabel: Yup.boolean(),
		ratingMode: Yup.string(),
		alternateId: Yup.string(),
		USPSRequestMappingMode: Yup.string(),
	});

	const uspsShipValidationSchema = Yup.object<models.IUSPSShipConfiguration>({
		LabelOwner_CRID: Yup.string()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSLabelOwnerCRID-label'))
			.required()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-account.config.crid-length-validation'),
				(val) => !val || (val && val.toString().length <= 15)
			)
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-account.config.integer-validation')),
		LabelOwner_MID: Yup.string()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSMID-label'))
			.required()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation'),
				(val) => !val || (val && (val.toString().length === 6 || val.toString().length === 9))
			)
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-account.config.integer-validation')),
		LabelOwner_ManifestMID: Yup.string()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSManifestMID-label'))
			.required()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation'),
				(val) => !val || (val && (val.toString().length === 6 || val.toString().length === 9))
			)
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-account.config.integer-validation')),
		ReturnLabelOwner_CRID: Yup.string()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSReturnLabelOwnerCRID-label'))
			.notRequired()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-account.config.crid-length-validation'),
				(val) => !val || (val && val.toString().length <= 15)
			)
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-account.config.integer-validation')),
		ReturnLabelOwner_MID: Yup.string()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSMID-label'))
			.notRequired()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation'),
				(val) => !val || (val && (val.toString().length === 6 || val.toString().length === 9))
			)
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-account.config.integer-validation')),
		ReturnLabelOwner_ManifestMID: Yup.string()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSManifestMID-label'))
			.notRequired()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation'),
				(val) => !val || (val && (val.toString().length === 6 || val.toString().length === 9))
			)
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-account.config.integer-validation')),
		MailOwner_MID: Yup.string()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSMailOwnerMID-label'))
			.notRequired()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation'),
				(val) => !val || (val && (val.toString().length === 6 || val.toString().length === 9))
			)
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-account.config.integer-validation')),
		Shipper_CRID: Yup.string()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSShipperCRID-label'))
			.notRequired()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-account.config.crid-length-validation'),
				(val) => !val || (val && val.toString().length <= 15)
			)
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-account.config.integer-validation')),
		Shipper_MID: Yup.string()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSShipperMID-label'))
			.notRequired()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation'),
				(val) => !val || (val && (val.toString().length === 6 || val.toString().length === 9))
			)
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-account.config.integer-validation')),
		Payer_CRID: Yup.string()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSPayerCRID-label'))
			.required()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-account.config.crid-length-validation'),
				(val) => !val || (val && val.toString().length <= 15)
			)
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-account.config.integer-validation')),
		Payer_AccountType: Yup.mixed<USPSPaymentAccountTypeEnum>()
			.required()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSPaymentAccountType-label')),
		Payer_EPSAccountNumber: Yup.string()
			.notRequired()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSEPSAccountNumber-label'))
			.test(
				'eps-number-required-with-eps',
				intlService.GetFormattedMessage('app.carrier-account.config.eps-number-required-with-eps'),
				function (value) {
					const { Payer_AccountType } = this.parent;
					const isCurrentFieldEmpty = !value;

					// if account type is EPS then EPS number is required
					return !(Payer_AccountType === USPSPaymentAccountTypeEnum.EPS && isCurrentFieldEmpty);
				}
			),
		Payer_PermitNumber: Yup.string()
			.notRequired()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSPermitNumber-label'))
			.test(
				'permit-number-required-with-permit',
				intlService.GetFormattedMessage('app.carrier-account.config.permit-number-required-with-permit'),
				function (value) {
					const { Payer_AccountType } = this.parent;
					const isCurrentFieldEmpty = !value;

					// if account type is Permit then Permit Number is required
					return !(Payer_AccountType === USPSPaymentAccountTypeEnum.Permit && isCurrentFieldEmpty);
				}
			),
		Payer_PermitZIP: Yup.string()
			.notRequired()
			.label(intlService.GetFormattedMessage('app.carrier-account.config.USPSPermitZIP-label'))
			.test(
				'permit-zip-required-with-permit',
				intlService.GetFormattedMessage('app.carrier-account.config.permit-zip-required-with-permit'),
				function (value) {
					const { Payer_AccountType } = this.parent;
					const isCurrentFieldEmpty = !value;

					// if account type is Permit then Permit Zip is required
					return !(Payer_AccountType === USPSPaymentAccountTypeEnum.Permit && isCurrentFieldEmpty);
				}
			)
			.length(5, intlService.GetFormattedMessage('app.carrier-account.config.permit-zip-length-validation')),
	});

	// All usps-ship configs are admin only
	if (carrierCode === CarriersEnum.USPSShip && accountLevel === AccountLevelEnum.Admin) {
		schemaValidation = schemaValidation.concat(uspsShipValidationSchema);
	}

	return schemaValidation;
};

export const getUpsNativeConfigurationSchema = (
	sequenceNumberRange: models.ISequenceNumberRange,
	preloadedData: ICarrierAccountConfiguration
) => {
	const schema = Yup.object().shape<models.IUPSNativeConfiguration>({
		accountNumber: Yup.string()
			.length(6, intlService.GetFormattedMessage('app.carrier-account-number-max-length'))
			.required(intlService.GetFormattedMessage('app.carrier-account-number-required'))
			.matches(OnlyNumberAndLetterRegex, intlService.GetFormattedMessage('app.carrier-account-number-regex')),
		bookNumber: Yup.string()
			.length(7, intlService.GetFormattedMessage('app.carrier-book-number-max-length'))
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-book-number-regex'))
			.required(intlService.GetFormattedMessage('app.carrier-book-number-required')),
		pageNumber: Yup.string()
			.length(2, intlService.GetFormattedMessage('app.carrier-page-number-max-length'))
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-page-number-regex'))
			.required(intlService.GetFormattedMessage('app.carrier-page-number-required')),
		sequenceNumberStart: Yup.number()
			.required(intlService.GetFormattedMessage('app.carrier-sequence-number-start'))
			.min(
				sequenceNumberRange.sequenceNumberMin,
				intlService.GetFormattedMessage('app.carrier-sequence-number-range-start', {
					0: sequenceNumberRange.sequenceNumberMin,
				})
			)
			.max(
				sequenceNumberRange.sequenceNumberMax,
				intlService.GetFormattedMessage('app.carrier-sequence-number-range-end', {
					0: sequenceNumberRange.sequenceNumberMax,
				})
			),
		sequenceNumberEnd: Yup.number()
			.required(intlService.GetFormattedMessage('app.carrier-sequence-number-end'))
			.min(
				sequenceNumberRange.sequenceNumberMin + 1,
				intlService.GetFormattedMessage('app.carrier-sequence-number-range-end-start', {
					0: sequenceNumberRange.sequenceNumberMin,
				})
			)
			.max(
				sequenceNumberRange.sequenceNumberMax,
				intlService.GetFormattedMessage('app.carrier-sequence-number-range-end', {
					0: sequenceNumberRange.sequenceNumberMax,
				})
			),
		sequenceNumber: Yup.number()
			.required(intlService.GetFormattedMessage('app.carrier-sequence-number'))
			.min(
				sequenceNumberRange.sequenceNumberMin,
				intlService.GetFormattedMessage('app.carrier-sequence-number-range-current-from-start', {
					0: sequenceNumberRange.sequenceNumberMin,
				})
			)
			.max(
				sequenceNumberRange.sequenceNumberMax,
				intlService.GetFormattedMessage('app.carrier-sequence-number-range-current-from-end', {
					0: sequenceNumberRange.sequenceNumberMax,
				})
			),
	});

	if (preloadedData == null) {
		return schema;
	}
	return schema.concat(
		Yup.object().shape<any>({
			sequenceNumber: Yup.number()
				.required(intlService.GetFormattedMessage('app.carrier-sequence-number'))
				.min(
					preloadedData.sequenceNumberStart,
					intlService.GetFormattedMessage('app.carrier-sequence-number-range-current-from-start', {
						0: preloadedData.sequenceNumberStart,
					})
				)
				.max(
					preloadedData.sequenceNumberEnd,
					intlService.GetFormattedMessage('app.carrier-sequence-number-range-current-from-end', {
						0: preloadedData.sequenceNumberEnd,
					})
				),
		})
	);
};

export const getFedExNativeConfigurationSchema = (
	sequenceNumberRange: models.ISequenceNumberRange,
	isCustomer: boolean,
	preloadedData?: IFedExNativeConfiguration
) => {
	const schema = Yup.object().shape<any>({
		isSmartPostEnabled: Yup.boolean(),
		shipperGroundAccountNumber: Yup.string()
			.nullable()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-fedex-ground-account-number-max-length'),
				(val) => !val || (val && (val.toString().length === 7 || val.toString().length === 9))
			)
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-fedex-ground-account-number-regex'))
			.when('isSmartPostEnabled', {
				is: true,
				then: Yup.string().required(intlService.GetFormattedMessage('app.carrier-ground-account-number-required')),
			})
			.test(
				'is-empty-or-all-populated',
				intlService.GetFormattedMessage('app.carrier-fedex-both-ground-account-number-requried'),
				function (value) {
					const { billingGroundAccountNumber } = this.parent;
					const isBillingGroundAccountNumberEmpty = !billingGroundAccountNumber;
					const isCurrentFieldEmpty = !value;

					if (isCurrentFieldEmpty) {
						return isBillingGroundAccountNumberEmpty;
					}

					return true;
				}
			),
		shipperAccountNumber: Yup.string()
			.length(9, intlService.GetFormattedMessage('app.carrier-fedex-account-number-max-length'))
			.required(intlService.GetFormattedMessage('app.carrier-account-number-required'))
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-fedex-account-number-regex')),
		billingGroundAccountNumber: Yup.string()
			.nullable()
			.test(
				'len',
				intlService.GetFormattedMessage('app.carrier-fedex-ground-account-number-max-length'),
				(val) => !val || (val && (val.toString().length === 7 || val.toString().length === 9))
			)
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-fedex-ground-account-number-regex'))
			.when('isSmartPostEnabled', {
				is: true,
				then: Yup.string().required(intlService.GetFormattedMessage('app.carrier-ground-account-number-required')),
			})
			.test(
				'is-empty-or-all-populated',
				intlService.GetFormattedMessage('app.carrier-fedex-both-ground-account-number-requried'),
				function (value) {
					const { shipperGroundAccountNumber } = this.parent;
					const isShipperGroundAccountNumberEmpty = !shipperGroundAccountNumber;
					const isCurrentFieldEmpty = !value;

					if (isCurrentFieldEmpty) {
						return isShipperGroundAccountNumberEmpty;
					}

					return true;
				}
			),
		billingAccountNumber: Yup.string()
			.length(9, intlService.GetFormattedMessage('app.carrier-fedex-account-number-max-length'))
			.required(intlService.GetFormattedMessage('app.carrier-account-number-required'))
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-fedex-account-number-regex')),
		meterNumber: Yup.string()
			.length(9, intlService.GetFormattedMessage('app.carrier-meter-number-max-length'))
			.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-meter-number-regex'))
			.required(intlService.GetFormattedMessage('app.carrier-meter-number-required')),
		smartPostId: Yup.string()
			.nullable()
			.when('isSmartPostEnabled', {
				is: true,
				then: Yup.string()
					.required(intlService.GetFormattedMessage('app.carrier-smartpost-id-number-required'))
					.length(5, intlService.GetFormattedMessage('app.carrier-smartpost-id-max-length'))
					.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-smartpost-id-number-regex')),
			}),
		smartPostHubId: Yup.string()
			.nullable()
			.when('isSmartPostEnabled', {
				is: true,
				then: Yup.string()
					.required(intlService.GetFormattedMessage('app.carrier-smartpost-hubId-required'))
					.length(4, intlService.GetFormattedMessage('app.carrier-smartpost-hubId-max-length'))
					.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-smartpost-hubId-regex')),
			}),
		smartPostMailerId: Yup.string()
			.nullable()
			.when('isSmartPostEnabled', {
				is: true,
				then: Yup.string()
					.required(intlService.GetFormattedMessage('app.carrier-smartpost-mailerId-required'))
					.length(9, intlService.GetFormattedMessage('app.carrier-smartpost-mailerId-max-length'))
					.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-smartpost-mailerId-regex')),
			}),
		smartPostDistributionCenter: Yup.string()
			.nullable()
			.when('isSmartPostEnabled', {
				is: true,
				then: Yup.string()
					.required(intlService.GetFormattedMessage('app.carrier-smartpost-distributionCenter-required'))
					.length(2, intlService.GetFormattedMessage('app.carrier-smartpost-distributionCenter-max-length'))
					.matches(OnlyNumberRegex, intlService.GetFormattedMessage('app.carrier-smartpost-distributionCenter-regex')),
			}),
		ratingMode: Yup.string(),
	});

	if (isCustomer || preloadedData === null) {
		return schema;
	}

	return schema.concat(
		Yup.object().shape<any>({
			sequenceNumberStart: Yup.number()
				.nullable()
				.min(
					sequenceNumberRange.sequenceNumberMin,
					intlService.GetFormattedMessage('app.carrier-sequence-number-range-start', {
						0: sequenceNumberRange.sequenceNumberMin,
					})
				)
				.max(
					sequenceNumberRange.sequenceNumberMax,
					intlService.GetFormattedMessage('app.carrier-sequence-number-range-end', {
						0: sequenceNumberRange.sequenceNumberMax,
					})
				)
				.test(
					'is-empty-or-all-populated',
					intlService.GetFormattedMessage('app.carrier-sequenceNumberStart-required'),
					function (value) {
						const { sequenceNumberEnd, sequenceNumber } = this.parent;
						const isSequenceNumberEndEmpty = !sequenceNumberEnd;
						const isSequenceNumberEmpty = !sequenceNumber;
						const isCurrentFieldEmpty = !value;

						if (isCurrentFieldEmpty) {
							return isSequenceNumberEndEmpty && isSequenceNumberEmpty;
						}
						if ((!isSequenceNumberEndEmpty || !isSequenceNumberEmpty) && isCurrentFieldEmpty) {
							return false;
						}
						return true;
					}
				),
			sequenceNumberEnd: Yup.number()
				.nullable()
				.min(
					sequenceNumberRange.sequenceNumberMin,
					intlService.GetFormattedMessage('app.carrier-sequence-number-range-end-start', {
						0: sequenceNumberRange.sequenceNumberMin,
					})
				)
				.max(
					sequenceNumberRange.sequenceNumberMax,
					intlService.GetFormattedMessage('app.carrier-sequence-number-range-end', {
						0: sequenceNumberRange.sequenceNumberMax,
					})
				)
				.moreThan(
					Yup.ref('sequenceNumberStart'),
					intlService.GetFormattedMessage('app.carrier-sequence-number-range-end-greater-than-start')
				)
				.test(
					'is-empty-or-all-populated',
					intlService.GetFormattedMessage('app.carrier-sequenceNumberEnd-required'),
					function (value) {
						const { sequenceNumberStart, sequenceNumber } = this.parent;
						const isSequenceNumberStartEmpty = !sequenceNumberStart;
						const isSequenceNumberEmpty = !sequenceNumber;
						const isCurrentFieldEmpty = !value;

						if (isCurrentFieldEmpty) {
							return isSequenceNumberStartEmpty && isSequenceNumberEmpty;
						}
						if ((!isSequenceNumberStartEmpty || !isSequenceNumberEmpty) && isCurrentFieldEmpty) {
							return false;
						}
						return true;
					}
				),
			sequenceNumber: Yup.number()
				.nullable()
				.min(
					Yup.ref('sequenceNumberStart'),
					intlService.GetFormattedMessage('app.carrier-sequence-number-greater-than-start')
				)
				.max(Yup.ref('sequenceNumberEnd'), intlService.GetFormattedMessage('app.carrier-sequence-number-less-than-end'))
				.test(
					'is-empty-or-all-populated',
					intlService.GetFormattedMessage('app.carrier-sequenceNumber-required'),
					function (value) {
						const { sequenceNumberEnd, sequenceNumberStart } = this.parent;
						const isSequenceNumberEndEmpty = !sequenceNumberEnd;
						const isSequenceNumberStartEmpty = !sequenceNumberStart;
						const isCurrentFieldEmpty = !value;

						if (isCurrentFieldEmpty) {
							return isSequenceNumberEndEmpty && isSequenceNumberStartEmpty;
						}
						if ((!isSequenceNumberEndEmpty || !isSequenceNumberStartEmpty) && isCurrentFieldEmpty) {
							return false;
						}
						return true;
					}
				),
		})
	);
};

export const getCarrierDetailsSchema = (isAppIdOptional: boolean) => {
	const schema = Yup.object().shape<models.ICarrierDetails>({
		name: Yup.string()
			.trim()
			.max(100, intlService.GetFormattedMessage('app.carrier-name-max-length-label'))
			.required(intlService.GetFormattedMessage('app.carrier-details-name-required-validation')),
		description: Yup.string()
			.trim()
			.max(1000, intlService.GetFormattedMessage('app.carrier-description-max-length-label'))
			.required(intlService.GetFormattedMessage('app.carrier-details-description-required-validation')),
		carrierDescription: Yup.string(),
	});

	if (isAppIdOptional) {
		return schema;
	}

	return schema.concat(
		Yup.object().shape<any>({
			appId: Yup.string()
				.trim()
				.required(intlService.GetFormattedMessage('app.carrier-details-app-required-validation')),
		})
	);
};

export const tierOverrideConfigSchema = (carrierCode: CarriersEnum) => {
	const validationSchemas: Array<Yup.ObjectSchema<object & models.ITierOverrideConfigs, object>> = [];

	// MailOwnerMID is shown for all of 'em
	validationSchemas.push(
		Yup.object().shape<Partial<models.ITierOverrideConfigs>>({
			MailOwner_MID: Yup.string()
				.notRequired()
				.matches(OnlyNumberRegex, intlService.GetFormattedMessage('usps-tier-config.MID-type-validation'))
				.test(
					'len',
					intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation'),
					(val) => !val || (val && (val.toString().length === 6 || val.toString().length === 9))
				),
		})
	);

	//  These 3 are only used for eVS
	if (carrierCode === CarriersEnum.USPSeVS) {
		validationSchemas.push(
			Yup.object().shape<Partial<models.ITierOverrideConfigs>>({
				USPSeVSWebtoolId: Yup.string()
					.notRequired()
					.max(100, intlService.GetFormattedMessage('usps-tier-config.ePostageWebtoolId-length-validation')),
				USPSeVSWebtoolPassword: Yup.string()
					.notRequired()
					.max(100, intlService.GetFormattedMessage('usps-tier-config.ePostageWebtoolPassword-length-validation')),

				USPSeVSWebtoolMID: Yup.string()
					.notRequired()
					.matches(OnlyNumberRegex, intlService.GetFormattedMessage('usps-tier-config.MID-type-validation'))
					.test(
						'len',
						intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation'),
						(val) => !val || (val && (val.toString().length === 6 || val.toString().length === 9))
					),
			})
		);
	}

	// USPSeVSMID is only used in eVS Native
	if (carrierCode === CarriersEnum.USPSeVSNative) {
		validationSchemas.push(
			Yup.object().shape<Partial<models.ITierOverrideConfigs>>({
				USPSeVSMID: Yup.string()
					.notRequired()
					.matches(OnlyNumberRegex, intlService.GetFormattedMessage('usps-tier-config.MID-type-validation'))
					.test(
						'len',
						intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation'),
						(val) => !val || (val && (val.toString().length === 6 || val.toString().length === 9))
					),
			})
		);
	}

	if (carrierCode === CarriersEnum.USPSShip) {
		validationSchemas.push(
			Yup.object().shape<Partial<models.ITierOverrideConfigs>>({
				ConsumerKey: Yup.string()
					.notRequired()
					.test(
						'is-empty-or-all-populated',
						intlService.GetFormattedMessage('app.carrier-account.config.consumer-key-secret-both-required'),
						function (value) {
							const { ConsumerSecret } = this.parent;
							const isConsumerSecretEmpty = !ConsumerSecret;
							const isCurrentFieldEmpty = !value;

							if (isCurrentFieldEmpty) {
								return isConsumerSecretEmpty;
							}

							return true;
						}
					),

				ConsumerSecret: Yup.string()
					.notRequired()
					.test(
						'is-empty-or-all-populated',
						intlService.GetFormattedMessage('app.carrier-account.config.consumer-key-secret-both-required'),
						function (value) {
							const { ConsumerKey } = this.parent;
							const isConsumerKey = !ConsumerKey;
							const isCurrentFieldEmpty = !value;

							if (isCurrentFieldEmpty) {
								return isConsumerKey;
							}

							return true;
						}
					),
			})
		);
	}

	return mergeValidationSchemas(validationSchemas);
};



// WEBPACK FOOTER //
// ./src/components/carriers/carriers.schema.ts